import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
	type MasterCloudProvider,
	type MasterContractType,
	type MasterCreateInput,
	type MasterDepartment,
	type MasterEnvironment,
	type MasterGeoLocation,
	type MasterLeadershipType,
	type MasterPersonType,
	type MasterUpdateInput,
	masterDataApi,
} from '../api/master-data.api'
import { masterDataKeys } from './query-keys'

// Re-exports so external consumers (the People feature's EmployeesPage form pickers)
// continue to import types from the same module they import the hooks from.
export type {
	MasterCloudProvider,
	MasterContractType,
	MasterCreateInput,
	MasterDepartment,
	MasterEnvironment,
	MasterGeoLocation,
	MasterLeadershipType,
	MasterPersonType,
	MasterRecord,
	MasterUpdateInput,
} from '../api/master-data.api'

// ─── Query Options ────────────────────────────────────────────────────────────

// Master data rarely changes, so we can cache it aggressively
const MASTER_DATA_STALE_TIME = 5 * 60 * 1000 // 5 minutes
const MASTER_DATA_CACHE_TIME = 30 * 60 * 1000 // 30 minutes

// ─── Contract Types ───────────────────────────────────────────────────────────

export const useContractTypes = () =>
	useQuery({
		queryKey: masterDataKeys.contractTypes.list(),
		queryFn: () => masterDataApi.list<MasterContractType>('contract-types'),
		staleTime: MASTER_DATA_STALE_TIME,
		gcTime: MASTER_DATA_CACHE_TIME,
	})

export const useCreateContractType = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: (data: MasterCreateInput) => masterDataApi.create<MasterContractType>('contract-types', data),
		onSuccess: () => void qc.invalidateQueries({ queryKey: masterDataKeys.contractTypes.all }),
	})
}

export const useUpdateContractType = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: MasterUpdateInput }) =>
			masterDataApi.update<MasterContractType>('contract-types', id, data),
		onSuccess: () => void qc.invalidateQueries({ queryKey: masterDataKeys.contractTypes.all }),
	})
}

export const useDeleteContractType = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: (id: string) => masterDataApi.delete('contract-types', id),
		onSuccess: () => void qc.invalidateQueries({ queryKey: masterDataKeys.contractTypes.all }),
	})
}

// ─── Geo Locations ────────────────────────────────────────────────────────────

export const useGeoLocations = () =>
	useQuery({
		queryKey: masterDataKeys.geoLocations.list(),
		queryFn: () => masterDataApi.list<MasterGeoLocation>('geo-locations'),
		staleTime: MASTER_DATA_STALE_TIME,
		gcTime: MASTER_DATA_CACHE_TIME,
	})

export const useCreateGeoLocation = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: (data: MasterCreateInput) => masterDataApi.create<MasterGeoLocation>('geo-locations', data),
		onSuccess: () => void qc.invalidateQueries({ queryKey: masterDataKeys.geoLocations.all }),
	})
}

export const useUpdateGeoLocation = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: MasterUpdateInput }) =>
			masterDataApi.update<MasterGeoLocation>('geo-locations', id, data),
		onSuccess: () => void qc.invalidateQueries({ queryKey: masterDataKeys.geoLocations.all }),
	})
}

export const useDeleteGeoLocation = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: (id: string) => masterDataApi.delete('geo-locations', id),
		onSuccess: () => void qc.invalidateQueries({ queryKey: masterDataKeys.geoLocations.all }),
	})
}

// ─── Departments ──────────────────────────────────────────────────────────────

export const useDepartments = () =>
	useQuery({
		queryKey: masterDataKeys.departments.list(),
		queryFn: () => masterDataApi.list<MasterDepartment>('departments'),
		staleTime: MASTER_DATA_STALE_TIME,
		gcTime: MASTER_DATA_CACHE_TIME,
	})

export const useCreateDepartment = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: (data: MasterCreateInput) => masterDataApi.create<MasterDepartment>('departments', data),
		onSuccess: () => void qc.invalidateQueries({ queryKey: masterDataKeys.departments.all }),
	})
}

export const useUpdateDepartment = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: MasterUpdateInput }) =>
			masterDataApi.update<MasterDepartment>('departments', id, data),
		onSuccess: () => void qc.invalidateQueries({ queryKey: masterDataKeys.departments.all }),
	})
}

export const useDeleteDepartment = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: (id: string) => masterDataApi.delete('departments', id),
		onSuccess: () => void qc.invalidateQueries({ queryKey: masterDataKeys.departments.all }),
	})
}

// ─── Environments ─────────────────────────────────────────────────────────────

export const useEnvironments = () =>
	useQuery({
		queryKey: masterDataKeys.environments.list(),
		queryFn: () => masterDataApi.list<MasterEnvironment>('environments'),
		staleTime: MASTER_DATA_STALE_TIME,
		gcTime: MASTER_DATA_CACHE_TIME,
	})

export const useCreateEnvironment = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: (data: MasterCreateInput) => masterDataApi.create<MasterEnvironment>('environments', data),
		onSuccess: () => void qc.invalidateQueries({ queryKey: masterDataKeys.environments.all }),
	})
}

export const useUpdateEnvironment = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: MasterUpdateInput }) =>
			masterDataApi.update<MasterEnvironment>('environments', id, data),
		onSuccess: () => void qc.invalidateQueries({ queryKey: masterDataKeys.environments.all }),
	})
}

export const useDeleteEnvironment = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: (id: string) => masterDataApi.delete('environments', id),
		onSuccess: () => void qc.invalidateQueries({ queryKey: masterDataKeys.environments.all }),
	})
}

// ─── Cloud Providers ──────────────────────────────────────────────────────────

export const useCloudProviders = () =>
	useQuery({
		queryKey: masterDataKeys.cloudProviders.list(),
		queryFn: () => masterDataApi.list<MasterCloudProvider>('cloud-providers'),
		staleTime: MASTER_DATA_STALE_TIME,
		gcTime: MASTER_DATA_CACHE_TIME,
	})

export const useCreateCloudProvider = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: (data: MasterCreateInput) => masterDataApi.create<MasterCloudProvider>('cloud-providers', data),
		onSuccess: () => void qc.invalidateQueries({ queryKey: masterDataKeys.cloudProviders.all }),
	})
}

export const useUpdateCloudProvider = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: MasterUpdateInput }) =>
			masterDataApi.update<MasterCloudProvider>('cloud-providers', id, data),
		onSuccess: () => void qc.invalidateQueries({ queryKey: masterDataKeys.cloudProviders.all }),
	})
}

export const useDeleteCloudProvider = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: (id: string) => masterDataApi.delete('cloud-providers', id),
		onSuccess: () => void qc.invalidateQueries({ queryKey: masterDataKeys.cloudProviders.all }),
	})
}

// ─── Leadership Types ─────────────────────────────────────────────────────────

export const useLeadershipTypes = () =>
	useQuery({
		queryKey: masterDataKeys.leadershipTypes.list(),
		queryFn: () => masterDataApi.list<MasterLeadershipType>('leadership-types'),
		staleTime: MASTER_DATA_STALE_TIME,
		gcTime: MASTER_DATA_CACHE_TIME,
	})

export const useCreateLeadershipType = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: (data: MasterCreateInput) => masterDataApi.create<MasterLeadershipType>('leadership-types', data),
		onSuccess: () => void qc.invalidateQueries({ queryKey: masterDataKeys.leadershipTypes.all }),
	})
}

export const useUpdateLeadershipType = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: MasterUpdateInput }) =>
			masterDataApi.update<MasterLeadershipType>('leadership-types', id, data),
		onSuccess: () => void qc.invalidateQueries({ queryKey: masterDataKeys.leadershipTypes.all }),
	})
}

export const useDeleteLeadershipType = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: (id: string) => masterDataApi.delete('leadership-types', id),
		onSuccess: () => void qc.invalidateQueries({ queryKey: masterDataKeys.leadershipTypes.all }),
	})
}

// ─── Person Types ─────────────────────────────────────────────────────────────

export const usePersonTypes = () =>
	useQuery({
		queryKey: masterDataKeys.personTypes.list(),
		queryFn: () => masterDataApi.list<MasterPersonType>('person-types'),
		staleTime: MASTER_DATA_STALE_TIME,
		gcTime: MASTER_DATA_CACHE_TIME,
	})

export const useCreatePersonType = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: (data: MasterCreateInput) => masterDataApi.create<MasterPersonType>('person-types', data),
		onSuccess: () => void qc.invalidateQueries({ queryKey: masterDataKeys.personTypes.all }),
	})
}

export const useUpdatePersonType = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: MasterUpdateInput }) =>
			masterDataApi.update<MasterPersonType>('person-types', id, data),
		onSuccess: () => void qc.invalidateQueries({ queryKey: masterDataKeys.personTypes.all }),
	})
}

export const useDeletePersonType = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: (id: string) => masterDataApi.delete('person-types', id),
		onSuccess: () => void qc.invalidateQueries({ queryKey: masterDataKeys.personTypes.all }),
	})
}
